import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid';

/**
 * What the admin panel's grids (the Users and Companies tabs) share: a double-click copies a cell's
 * shown value, and the cells that hold a picture or a button are left out of that, unselectable and
 * dimmed. Types only from the grid package, so the tabs keep their own imports of it.
 */

/** The class of a cell a double-click does not copy: a picture or an action button. */
export const NO_SELECT_CELL = 'no-select';

/** How a cell that is not copied looks: unselectable, dimmed, with no focus ring. For a grid's `sx`. */
export const noSelectCellStyles = {
  [`& .${NO_SELECT_CELL}`]: {
    userSelect: 'none',
    color: '#999',
  },
  [`& .${NO_SELECT_CELL}:focus`]: {
    outline: 'none',
  },
};

/**
 * A grid's `onCellDoubleClick`: copies the cell's shown value to the clipboard and says so, unless the
 * cell is one that is not copied or holds something other than text or a number.
 */
export const copyCellOnDoubleClick =
  (notify: (message: string) => void) =>
  (params: { colDef: Pick<GridColDef, 'cellClassName'>; formattedValue?: unknown }): void => {
    if (params.colDef.cellClassName === NO_SELECT_CELL) return;
    const cellValue = params.formattedValue;
    if (typeof cellValue === 'string' || typeof cellValue === 'number') {
      void navigator.clipboard.writeText(String(cellValue)).then(() => {
        notify(`Copied cell value: ${cellValue}`);
      });
    }
  };

/** The `actions` column at the end of a row: its buttons, not copied. */
export const actionsColumn = <R extends GridValidRowModel>(column: Omit<GridColDef<R>, 'field' | 'headerName' | 'cellClassName'>): GridColDef<R> => ({
  field: 'actions',
  headerName: 'actions',
  cellClassName: NO_SELECT_CELL,
  ...column,
});
